/**
 * Internal Lima district dataset — single source of truth for the wholesale
 * signup form's district-driven auto-population (postal code / province /
 * country) and for the delivery-fee display used by the order-request form.
 *
 * Postal codes: Peru's modern 5-digit system (codigopostal.gob.pe) assigns
 * MULTIPLE codes per district based on exact address (up to a dozen or more
 * for larger districts) — there is no official 1:1 district-to-code mapping.
 * The value below is a single representative/primary code per district,
 * compiled from public references, used deliberately as a practical default
 * for wholesale delivery-zone purposes. If postal precision ever matters for
 * actual mail/shipping (as opposed to internal delivery zoning), verify the
 * exact code for a given street address at codigopostal.gob.pe.
 *
 * DELIVERY FEES — "Tarifas de delivery por zonas" map (2026-10-29). This is
 * the storefront's display-only copy, kept structurally identical to the
 * authoritative order-backend/src/config.js (same zones, same cent amounts,
 * same owner decisions documented there: Zona Sur charged at S/ 20,
 * Cieneguilla S/ 12, Salamanca S/ 15). The backend decides what is charged.
 *
 * IMPORTANT — two independent consumers of this same array:
 *  1. assets/whatsapp-checkout.js (Entrega district dropdown + fee display)
 *     — only districts with `entrega_supported: true` are offered.
 *  2. assets/wholesale-district-autofill.js (wholesale SIGNUP form's postal
 *     code / provincia / país autofill) — uses the FULL list regardless of
 *     delivery support, since a wholesale prospect can be based anywhere.
 *     Do NOT shrink this array down to only entrega_supported districts.
 *
 * Naming: the map's "Lima (Cercado)" is "Cercado de Lima"; "Surco" is the
 * short form of Santiago de Surco; "Magdalena" is Magdalena del Mar.
 * "Salamanca" is a sector within Ate, not an INEI district, kept as its own
 * entry with Ate's postal code as a placeholder.
 */

const DELIVERY_ZONES = {
  NORTE: { feeCents: 2000, districts: ['Comas', 'Los Olivos', 'Independencia'] },
  SAN_JUAN_DE_LURIGANCHO: { feeCents: 1800, districts: ['San Juan de Lurigancho'] },
  CHACLACAYO: { feeCents: 2200, districts: ['Chaclacayo'] },
  CALLAO: { feeCents: 2000, districts: ['Callao', 'Mi Perú', 'Bellavista', 'La Perla', 'La Punta', 'Carmen de la Legua'] },
  CENTRO: {
    feeCents: 2000,
    districts: ['Cercado de Lima', 'Jesús María', 'Lince', 'Pueblo Libre', 'Magdalena', 'San Miguel', 'Breña', 'La Victoria', 'San Martín de Porres'],
  },
  MIRAFLORES_ALREDEDORES: { feeCents: 1800, districts: ['Miraflores', 'San Isidro', 'Surquillo', 'Barranco'] },
  ESTE_LA_MOLINA: { feeCents: 1500, districts: ['La Molina', 'Surco', 'San Borja', 'Salamanca'] },
  ESTE_ATE: { feeCents: 1800, districts: ['Ate', 'San Luis', 'Santa Anita', 'El Agustino', 'Rímac'] },
  SUR: { feeCents: 2000, districts: ['San Juan de Miraflores', 'Chorrillos', 'Villa El Salvador', 'Villa María del Triunfo'] },
  CIENEGUILLA: { feeCents: 1200, districts: ['Cieneguilla'] },
};

const ENTREGA_FEE_CENTS = new Map();
for (const zone of Object.values(DELIVERY_ZONES)) {
  for (const district of zone.districts) {
    ENTREGA_FEE_CENTS.set(district, zone.feeCents);
  }
}

const DISTRICT_POSTAL_DATA = [
  // --- entrega_supported: true (in DELIVERY_ZONES) ---
  { district: 'Miraflores', postal_code: '15074' },
  { district: 'San Isidro', postal_code: '15073' },
  { district: 'Surco', postal_code: '15038' },
  { district: 'La Molina', postal_code: '15023' },
  { district: 'Barranco', postal_code: '15063' },
  { district: 'Chorrillos', postal_code: '15057' },
  { district: 'Jesús María', postal_code: '15072' },
  { district: 'Pueblo Libre', postal_code: '15084' },
  { district: 'Lince', postal_code: '15046' },
  { district: 'Cercado de Lima', postal_code: '15001' },
  { district: 'Los Olivos', postal_code: '15304' },
  { district: 'Comas', postal_code: '15311' },
  { district: 'Independencia', postal_code: '15331' },
  { district: 'Ate', postal_code: '15012' },
  { district: 'Santa Anita', postal_code: '15009' },
  { district: 'El Agustino', postal_code: '15006' },
  { district: 'San Juan de Lurigancho', postal_code: '15408' },
  { district: 'Chaclacayo', postal_code: '15472' },
  { district: 'Villa El Salvador', postal_code: '15842' },
  { district: 'Villa María del Triunfo', postal_code: '15809' },
  { district: 'San Juan de Miraflores', postal_code: '15801' },
  { district: 'Cieneguilla', postal_code: '15594' },
  { district: 'Rímac', postal_code: '15093' },
  { district: 'Callao', postal_code: '07001' },
  // Mi Perú postal code not independently confirmed via public sources
  // (a relatively new district split off Ventanilla in 2014); using a
  // Ventanilla-area code as a practical placeholder, flagged, not a
  // guess presented as fact.
  { district: 'Mi Perú', postal_code: '07071' },
  { district: 'Bellavista', postal_code: '07011' },
  { district: 'La Perla', postal_code: '07016' },
  { district: 'La Punta', postal_code: '07021' },
  { district: 'Carmen de la Legua', postal_code: '07006' },
  { district: 'Surquillo', postal_code: '15036' },
  { district: 'San Borja', postal_code: '15037' },
  { district: 'San Miguel', postal_code: '15087' },
  { district: 'Magdalena', postal_code: '15086' },
  { district: 'La Victoria', postal_code: '15033' },
  { district: 'San Martín de Porres', postal_code: '15109' },
  // Breña and San Luis: representative codes, not confirmed against
  // codigopostal.gob.pe (only used to prefill the signup form).
  { district: 'Breña', postal_code: '15083' },
  { district: 'San Luis', postal_code: '15021' },
  // Salamanca: sector of Ate, not an independent district — see file
  // header. Using Ate's postal code as a practical placeholder.
  { district: 'Salamanca', postal_code: '15012' },

  // --- entrega_supported: false ("no llegamos" or outside the map;
  // retained ONLY for wholesale-signup postal autofill, see file header) ---
  { district: 'Puente Piedra', postal_code: '15118' },
  { district: 'Carabayllo', postal_code: '15121' },
  { district: 'Lurín', postal_code: '15823' },
  { district: 'Pachacámac', postal_code: '15593' },
  { district: 'Santa Rosa', postal_code: '15123' },
  { district: 'Ancón', postal_code: '15122' },
  { district: 'Lurigancho', postal_code: '15457' },
];

// Callao and its constituent districts belong to the Provincia
// Constitucional del Callao, a separate province from Lima.
const CALLAO_PROVINCE_DISTRICTS = ['Callao', 'Mi Perú', 'Bellavista', 'La Perla', 'La Punta', 'Carmen de la Legua'];

window.LIMA_DISTRICTS = DISTRICT_POSTAL_DATA.map((entry) => {
  const feeCents = ENTREGA_FEE_CENTS.has(entry.district) ? ENTREGA_FEE_CENTS.get(entry.district) : null;
  return {
    district: entry.district,
    postal_code: entry.postal_code,
    province: CALLAO_PROVINCE_DISTRICTS.includes(entry.district) ? 'Callao' : 'Lima',
    country: 'Perú',
    delivery_zone: null,
    entrega_supported: feeCents !== null,
    delivery_fee_cents: feeCents,
  };
});
